import React from 'react';
import {
  UserCircleIcon,
  CarFrontIcon,
  CarIcon,
  HomeIcon,
  HousePlusIcon,
  SettingsIcon,
  ShieldCheckIcon,
  KeyRoundIcon,
  FileTextIcon,
} from 'lucide-react';
import type { PermissionCheck } from '@/features/auth';

export type NavItem = {
  name: string;
  icon: React.ReactNode;
  path?: string;
  requiredRoles?: string[];
  requiredPermissions?: PermissionCheck[];
  requiresInternal?: boolean;
  subItems?: {
    name: string;
    path: string;
    pro?: boolean;
    new?: boolean;
    requiredRoles?: string[];
    requiredPermissions?: PermissionCheck[];
  }[];
};

export const navItems: NavItem[] = [
  {
    icon: <HomeIcon />,
    name: 'Home',
    path: '/',
  },
  {
    icon: <CarFrontIcon />,
    name: 'Service Plans',
    subItems: [
      {
        name: 'Agreements',
        path: '/agreements',
        pro: false,
        requiredPermissions: [
          { resource: 'quote', action: 'browse' },
          { resource: 'plan', action: 'browse' },
        ],
      },
      {
        name: 'Create New',
        path: '/service-plans/new',
        pro: false,
        requiredPermissions: [{ resource: 'quote', action: 'browse' }],
      },
    ],
    requiredPermissions: [
      { resource: 'quote', action: 'browse' },
      { resource: 'plan', action: 'browse' },
    ],
  },
  // Maintenance Plans nav entry deferred: /maintenance-plans/new has no route implementation yet (see plan Out of Scope).
  {
    icon: <CarIcon />,
    name: 'Vehicles',
    subItems: [
      {
        name: 'Search',
        path: '/vehicles/search',
        requiredPermissions: [{ resource: 'vehicle', action: 'browse' }],
      },
    ],
  },
  // Reports nav entry deferred: /reports/sales has no route implementation yet (see plan Out of Scope).
  // Contracts nav entry deferred: /contracts/search has no route implementation yet (see plan Out of Scope).
];

export const adminItems: NavItem[] = [
  {
    icon: <SettingsIcon />,
    name: 'SMR Admin',
    requiresInternal: true,
    subItems: [
      {
        name: 'Products',
        path: '/smr-admin/products',
        requiredPermissions: [{ resource: 'smr', action: 'product.browse' }],
      },
      {
        name: 'Plans',
        path: '/smr-admin/plans',
        requiredPermissions: [{ resource: 'smr', action: 'plan.browse' }],
      },
      {
        name: 'Items',
        path: '/smr-admin/items',
        requiredPermissions: [{ resource: 'smr', action: 'item.browse' }],
      },
      {
        name: 'Fees',
        path: '/smr-admin/fees',
        requiredPermissions: [{ resource: 'smr', action: 'fee.browse' }],
      },
      {
        name: 'Config Options',
        path: '/smr-admin/config-options',
        requiredPermissions: [{ resource: 'smr', action: 'config-option.browse' }],
      },
      {
        name: 'Optional Extras',
        path: '/smr-admin/addons',
        requiredPermissions: [{ resource: 'smr', action: 'addon.browse' }],
      },
      {
        name: 'Optional Extra Groups',
        path: '/smr-admin/addon-groups',
        requiredPermissions: [{ resource: 'smr', action: 'addon-group.browse' }],
      },
    ],
  },
  {
    icon: <UserCircleIcon />,
    name: 'Users',
    path: '/admin/users',
    requiredPermissions: [{ resource: 'user', action: 'browse' }],
    subItems: [
      {
        name: 'User List',
        path: '/admin/users',
        requiredPermissions: [{ resource: 'user', action: 'browse' }],
      },
      {
        name: 'Add User',
        path: '/admin/users/new',
        requiredPermissions: [{ resource: 'user', action: 'add' }],
      },
    ],
  },
  {
    icon: <KeyRoundIcon />,
    name: 'Roles',
    path: '/admin/roles',
    requiredPermissions: [{ resource: 'role', action: 'browse' }],
  },
  {
    icon: <FileTextIcon />,
    name: 'Document templates',
    path: '/admin/document-templates',
    requiredPermissions: [{ resource: 'document_template', action: 'browse' }],
  },
  {
    icon: <HousePlusIcon />,
    name: 'Dealers',
    path: '/admin/dealers',
    requiresInternal: true,
    requiredPermissions: [{ resource: 'dealer', action: 'browse' }],
    subItems: [
      {
        name: 'Dealer List',
        path: '/admin/dealers',
        requiredPermissions: [{ resource: 'dealer', action: 'browse' }],
      },
      {
        name: 'Add Dealer',
        path: '/admin/dealers/new',
        requiredPermissions: [{ resource: 'dealer', action: 'add' }],
      },
      {
        name: 'Config Options',
        path: '/admin/dealers/config-options',
        requiredPermissions: [{ resource: 'dealer', action: 'config-option.browse' }],
      },
    ],
  },
  {
    icon: <ShieldCheckIcon />,
    name: 'Security Groups',
    path: '/admin/security-groups',
    requiredPermissions: [{ resource: 'security_group', action: 'browse' }],
    subItems: [
      {
        name: 'Security Groups',
        path: '/admin/security-groups',
        requiredPermissions: [{ resource: 'security_group', action: 'browse' }],
      },
      {
        name: 'New Group',
        path: '/admin/security-groups/new',
        requiredPermissions: [{ resource: 'security_group', action: 'add' }],
      },
    ],
  },
];
